/**
 * The delivery archive, read from Supabase.
 *
 * Replaces `mock.ts` as the thing `queries.ts`'s `source()` points at (A26).
 * The shapes below are deliberately built the same way `mock.ts` built them —
 * fetch everything published, flatten delivery + media into gallery items,
 * filter/sort/page in this layer — rather than attempted as one PostgREST
 * query. `public_delivery_media` and `public_deliveries` are two separate
 * views with no FK PostgREST can embed across, and the gallery pages by
 * MEDIUM across every delivery at once, which a per-delivery fetch (the
 * pattern `listVehicleMedia` uses for one listing's detail page) does not
 * give. The archive is a handful of handovers, not a marketplace catalogue,
 * so fetching it whole and slicing here costs nothing a visitor would notice.
 */
import { getSupabase } from '@/lib/supabase';

import { toApiError } from '../ApiError';

import {
  type DeliveryCaption,
  type DeliveryFacet,
  type DeliveryFacets,
  type DeliveryGalleryItem,
  type DeliveryListFilters,
  type DeliveryMedia,
  type DeliveryMediaFilter,
  type DeliveryOrientation,
  type DeliveryTestimonial,
  type Page,
} from './types';

const MEDIA_BUCKET = 'delivery-media';
const DEFAULT_PAGE_SIZE = 12;

/** Row shape of public.public_deliveries. */
type DeliveryRow = {
  id: string;
  slug: string;
  vehicle: string;
  destination: string;
  delivered_on: string;
  shows_client: boolean;
  testimonial_quote: string | null;
  testimonial_attribution: string | null;
  updated_at: string;
  tiktok_url: string | null;
  /** Cached from TikTok's own oEmbed thumbnail when staff save tiktok_url; null falls back to a plain placeholder. */
  tiktok_thumbnail_storage_path: string | null;
};

/** Row shape of public.public_delivery_media. */
type MediaRow = {
  id: string;
  delivery_id: string;
  kind: DeliveryMedia['kind'];
  storage_path: string;
  poster_storage_path: string | null;
  alt_text: string | null;
  position: number;
  width: number | null;
  height: number | null;
  duration_sec: number | null;
};

/** Row shape of public.public_delivery_facets — one row. */
type FacetRow = {
  destinations: { value: string; count: number }[] | null;
  media_counts: Record<DeliveryMediaFilter, number> | null;
  delivery_count: number;
};

function publicMediaUrl(path: string): string {
  return getSupabase().storage.from(MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** Unknown is drawn as landscape — the masonry grid needs some answer, and a wide tile is the least wrong guess. */
function orientationOf(width: number | null, height: number | null): DeliveryOrientation {
  if (width === null || height === null) return 'landscape';
  if (width === height) return 'square';
  return width > height ? 'landscape' : 'portrait';
}

function toCaption(row: DeliveryRow): DeliveryCaption {
  const testimonial: DeliveryTestimonial | null = row.testimonial_quote
    ? { quote: row.testimonial_quote, attribution: row.testimonial_attribution }
    : null;
  return {
    id: row.id,
    slug: row.slug,
    vehicle: row.vehicle,
    destination: row.destination,
    deliveredOn: row.delivered_on,
    showsClient: row.shows_client,
    testimonial,
  };
}

function toMedia(row: MediaRow): DeliveryMedia {
  return {
    id: row.id,
    kind: row.kind,
    src: publicMediaUrl(row.storage_path),
    poster: row.poster_storage_path ? publicMediaUrl(row.poster_storage_path) : null,
    alt: row.alt_text,
    width: row.width,
    height: row.height,
    durationSec: row.duration_sec,
    orientation: orientationOf(row.width, row.height),
    position: row.position,
  };
}

/**
 * A delivery's TikTok link, as a gallery item in its own right. There is no
 * `delivery_media` row behind it — `position` places it after the delivery's
 * own uploaded proof (its own photos/clips come first), and `orientation` is
 * stated rather than derived, matching TikTok's own fixed vertical shape.
 *
 * `poster` is a staff pick, not an automatic one: TikTok's oEmbed response
 * carries no thumbnail field in practice (confirmed 2026-11-01 — see
 * 20261101100000), so `tiktok_thumbnail_storage_path` is just a reference to
 * one of the delivery's OWN saved `delivery_media` rows, set from Kabex Hub
 * (`deliveriesApi.setTikTokPoster`). Null means nobody has picked one yet,
 * which `DeliveryTikTokTile` treats as "draw the plain placeholder", not an
 * error.
 */
function toTikTokMedia(row: DeliveryRow, position: number): DeliveryMedia {
  return {
    id: `${row.id}-tiktok`,
    kind: 'tiktok',
    src: row.tiktok_url as string,
    poster: row.tiktok_thumbnail_storage_path
      ? publicMediaUrl(row.tiktok_thumbnail_storage_path)
      : null,
    alt: null,
    width: null,
    height: null,
    durationSec: null,
    orientation: 'portrait',
    position,
  };
}

/**
 * The whole published archive: every handover newest first, and every medium
 * flattened against the caption it belongs to, cover first within it — the
 * same shape `mock.ts`'s module-level `deliveries`/`galleryItems` were.
 */
async function fetchArchive(): Promise<{
  deliveries: DeliveryCaption[];
  galleryItems: DeliveryGalleryItem[];
}> {
  const [deliveriesRes, mediaRes] = await Promise.all([
    getSupabase()
      .from('public_deliveries')
      .select('*')
      .order('delivered_on', { ascending: false })
      .returns<DeliveryRow[]>(),
    getSupabase().from('public_delivery_media').select('*').returns<MediaRow[]>(),
  ]);
  if (deliveriesRes.error) throw deliveriesRes.error;
  if (mediaRes.error) throw mediaRes.error;

  const deliveries = deliveriesRes.data.map(toCaption);
  const order = new Map(deliveries.map((d, index) => [d.id, index]));
  const mediaCountByDelivery = new Map<string, number>();
  for (const row of mediaRes.data) {
    mediaCountByDelivery.set(row.delivery_id, (mediaCountByDelivery.get(row.delivery_id) ?? 0) + 1);
  }

  // Whichever photo each delivery picked to back its TikTok tile — see
  // toTikTokMedia. That photo is spent on the TikTok tile and must NOT also
  // get its own gallery tile, or the wall shows the same photograph twice:
  // once standing on its own, once again as the TikTok tile's backdrop.
  const tiktokPosterPathByDelivery = new Map(
    deliveriesRes.data
      .filter((row): row is DeliveryRow & { tiktok_thumbnail_storage_path: string } =>
        Boolean(row.tiktok_thumbnail_storage_path),
      )
      .map((row) => [row.id, row.tiktok_thumbnail_storage_path]),
  );

  const mediaItems: DeliveryGalleryItem[] = mediaRes.data
    .filter((row) => order.has(row.delivery_id))
    .filter((row) => tiktokPosterPathByDelivery.get(row.delivery_id) !== row.storage_path)
    .map((row) => ({
      media: toMedia(row),
      delivery: deliveries[order.get(row.delivery_id) as number] as DeliveryCaption,
    }));

  // One more tile per delivery that has a TikTok link, placed after that
  // delivery's own uploaded proof — see toTikTokMedia.
  const tiktokItems: DeliveryGalleryItem[] = deliveriesRes.data
    .filter((row) => row.tiktok_url)
    .map((row) => ({
      media: toTikTokMedia(row, mediaCountByDelivery.get(row.id) ?? 0),
      delivery: deliveries[order.get(row.id) as number] as DeliveryCaption,
    }));

  const galleryItems = [...mediaItems, ...tiktokItems].sort((a, b) => {
    const byDelivery = (order.get(a.delivery.id) ?? 0) - (order.get(b.delivery.id) ?? 0);
    return byDelivery !== 0 ? byDelivery : a.media.position - b.media.position;
  });

  return { deliveries, galleryItems };
}

const matches = (item: DeliveryGalleryItem, filters: DeliveryListFilters): boolean => {
  if (filters.destination && item.delivery.destination !== filters.destination) return false;
  switch (filters.media ?? 'all') {
    case 'photos':
      return item.media.kind === 'image';
    case 'videos':
      // A TikTok link counts as a video — the product decision behind this
      // feature, and what public_delivery_facets' own "videos" tally assumes.
      return item.media.kind === 'video' || item.media.kind === 'tiktok';
    case 'testimonies':
      return item.delivery.testimonial !== null;
    case 'all':
      return true;
  }
};

export async function listDeliveryMedia(
  filters: DeliveryListFilters = {},
): Promise<Page<DeliveryGalleryItem>> {
  const page = Math.max(1, filters.page ?? 1);
  const pageSize = Math.max(1, filters.pageSize ?? DEFAULT_PAGE_SIZE);
  const start = (page - 1) * pageSize;

  let galleryItems: DeliveryGalleryItem[];
  try {
    ({ galleryItems } = await fetchArchive());
  } catch (error) {
    throw toApiError(error);
  }

  const matched = galleryItems.filter((item) => matches(item, filters));
  return {
    items: matched.slice(start, start + pageSize),
    total: matched.length,
    page,
    pageSize,
  };
}

/**
 * The newest handovers' best medium, for the proof strips on other pages.
 *
 * A synthesized TikTok tile never wins the cover: those strips open into
 * `useDeliveryViewers`' lightbox/reel stacks, which a TikTok embed's fixed
 * shape and missing play/API control were never built to join — so a
 * TikTok-only delivery (no photo or clip at all) simply sits out the proof
 * strips rather than being forced into a viewer that cannot show it. The same
 * now also applies to a delivery whose only photo was spent on its TikTok
 * poster (see `tiktokPosterPathByDelivery` in `fetchArchive`): `galleryItems`
 * has nothing left of it but the TikTok tile, so it sits out here too.
 */
export async function listDeliveryShowcase(limit = 6): Promise<DeliveryGalleryItem[]> {
  let deliveries: DeliveryCaption[];
  let galleryItems: DeliveryGalleryItem[];
  try {
    ({ deliveries, galleryItems } = await fetchArchive());
  } catch (error) {
    throw toApiError(error);
  }

  const covers: DeliveryGalleryItem[] = [];
  for (const delivery of deliveries) {
    const cover = galleryItems.find(
      (item) => item.delivery.id === delivery.id && item.media.kind !== 'tiktok',
    );
    if (cover) covers.push(cover);
    if (covers.length >= limit) break;
  }
  return covers;
}

export async function getDeliveryFacets(): Promise<DeliveryFacets> {
  let row: FacetRow | null;
  try {
    const { data, error } = await getSupabase()
      .from('public_delivery_facets')
      .select('*')
      .maybeSingle<FacetRow>();
    if (error) throw error;
    row = data;
  } catch (error) {
    throw toApiError(error);
  }

  const destinations: DeliveryFacet[] = (row?.destinations ?? []).map((d) => ({
    value: d.value,
    count: d.count,
  }));

  const mediaCounts = {
    all: row?.media_counts?.all ?? 0,
    photos: row?.media_counts?.photos ?? 0,
    videos: row?.media_counts?.videos ?? 0,
    testimonies: row?.media_counts?.testimonies ?? 0,
  } satisfies Record<DeliveryMediaFilter, number>;

  return { destinations, mediaCounts, deliveryCount: row?.delivery_count ?? 0 };
}
